import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Pencil } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useBag } from "@/context/BagContext";
import { api } from "@/lib/api";
import { COLOR_META, SHIPPING, formatPrice } from "@/lib/meta";
import Placeholder from "@/components/Placeholder";

const STEP_KEYS = ["contact", "shipping", "delivery", "payment", "summary"];

export default function Checkout() {
  const { t, lang } = useLang();
  const { items, subtotal, clear } = useBag();
  const [step, setStep] = useState(0);
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    address: "", city: "", postal: "",
    delivery: "standard", payment: "cod",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fee =
    form.delivery === "express"
      ? SHIPPING.EXPRESS_FEE
      : subtotal >= SHIPPING.FREE_THRESHOLD
      ? 0
      : SHIPPING.STANDARD_FEE;
  const total = subtotal + fee;

  if (done) {
    return (
      <div className="py-28 text-center px-6" data-testid="checkout-success">
        <p className="font-serif font-light text-5xl lg:text-6xl text-ink mb-6">{t.checkout.successTitle}</p>
        <p className="text-ink/65 max-w-md mx-auto leading-relaxed">
          {t.checkout.successSub.replace("{n}", done.number)}
        </p>
        <p className="font-serif text-2xl text-rose-deep mt-8" data-testid="checkout-order-number">{done.number}</p>
        <div className="max-w-sm mx-auto mt-10 bg-cream border border-ink/10 rounded-sm p-6 text-sm space-y-2">
          <div className="flex justify-between"><span className="text-ink/60">{t.bag.total}</span><span data-testid="checkout-success-total">{formatPrice(done.total)}</span></div>
          <p className="text-[11px] text-ink/45 pt-2 border-t border-ink/10">{t.checkout.successNote}</p>
        </div>
        <Link to="/boutique" className="btn btn-ink mt-10" data-testid="checkout-success-back">{t.checkout.backToShop}</Link>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-32 text-center px-6" data-testid="checkout-empty">
        <p className="font-serif text-3xl text-ink/70 mb-8">{t.checkout.emptyBag}</p>
        <Link to="/boutique" className="btn btn-ink" data-testid="checkout-empty-cta">{t.bag.ctaShop}</Link>
      </div>
    );
  }

  const validate = () => {
    if (step === 0) {
      if (!form.name.trim() || !form.email.includes("@") || !form.phone.trim()) {
        toast.error(t.checkout.errRequired);
        return false;
      }
    }
    if (step === 1) {
      if (!form.address.trim() || !form.city.trim()) {
        toast.error(t.checkout.errRequired);
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(4, s + 1));
    window.scrollTo(0, 0);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({ slug: i.slug, size: i.size, color: i.color, qty: i.qty })),
        contact: { name: form.name, email: form.email, phone: form.phone },
        shipping: { address: form.address, city: form.city, postal: form.postal },
        delivery: form.delivery,
        payment: form.payment,
      };
      const res = await api.post("/orders", payload);
      setDone(res.data);
      clear();
      window.scrollTo(0, 0);
    } catch {
      toast.error(t.contact.err);
    }
    setPlacing(false);
  };

  const deliveryLabel = form.delivery === "express" ? t.checkout.express : t.checkout.standard;
  const paymentLabel = form.payment === "cod" ? t.checkout.cod : t.checkout.transfer;

  return (
    <div className="py-12 lg:py-16 pb-24" data-testid="checkout-page">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
        <h1 className="font-serif font-light text-4xl lg:text-5xl text-ink">{t.checkout.title}</h1>
        <p className="mt-2 text-ink/55 text-sm">{t.checkout.sub}</p>

        {/* progress */}
        <div className="mt-10 mb-12 flex items-center" data-testid="checkout-progress">
          {STEP_KEYS.map((k, i) => (
            <div key={k} className="flex-1 flex items-center last:flex-none">
              <button
                onClick={() => i < step && setStep(i)}
                data-testid={`checkout-step-dot-${k}`}
                className={`w-9 h-9 rounded-full border flex items-center justify-center text-[12px] transition-all duration-300 shrink-0 ${
                  i < step
                    ? "bg-rose-deep text-ivory border-rose-deep cursor-pointer"
                    : i === step
                    ? "border-ink bg-ink text-ivory"
                    : "border-ink/25 text-ink/40"
                }`}
                aria-label={t.checkout[`step${k.charAt(0).toUpperCase()}${k.slice(1)}`]}
              >
                {i < step ? <Check className="w-4 h-4" strokeWidth={2} /> : i + 1}
              </button>
              {i < STEP_KEYS.length - 1 && (
                <span className={`h-px flex-1 mx-2 ${i < step ? "bg-rose-deep" : "bg-ink/15"}`} />
              )}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-12 items-start">
          <div>
            {step === 0 && (
              <div className="space-y-6" data-testid="checkout-step-contact">
                <StepTitle>{t.checkout.stepContact}</StepTitle>
                <Field label={t.checkout.f_name} value={form.name} onChange={set("name")} id="checkout-field-name" />
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label={t.checkout.f_email} value={form.email} onChange={set("email")} type="email" id="checkout-field-email" />
                  <Field label={t.checkout.f_phone} value={form.phone} onChange={set("phone")} type="tel" id="checkout-field-phone" />
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6" data-testid="checkout-step-shipping">
                <StepTitle>{t.checkout.stepShipping}</StepTitle>
                <Field label={t.checkout.f_address} value={form.address} onChange={set("address")} id="checkout-field-address" />
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label={t.checkout.f_city} value={form.city} onChange={set("city")} id="checkout-field-city" />
                  <Field label={t.checkout.f_postal} value={form.postal} onChange={set("postal")} id="checkout-field-postal" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="checkout-step-delivery">
                <StepTitle>{t.checkout.deliveryTitle}</StepTitle>
                <div className="space-y-4 mt-2">
                  {[
                    { id: "standard", title: t.checkout.standard, time: t.checkout.standardTime, desc: t.checkout.standardDesc },
                    { id: "express", title: t.checkout.express, time: t.checkout.expressTime, desc: t.checkout.expressDesc },
                  ].map((o) => (
                    <button
                      key={o.id}
                      onClick={() => setForm((f) => ({ ...f, delivery: o.id }))}
                      data-testid={`checkout-delivery-${o.id}`}
                      className={`w-full text-start border rounded-sm p-5 transition-all duration-200 ${
                        form.delivery === o.id ? "border-rose-deep bg-rose/[0.06]" : "border-ink/20 hover:border-ink/40"
                      }`}
                    >
                      <span className="flex items-center justify-between">
                        <span className="font-serif text-lg text-ink">
                          {o.title} <span className="text-ink/45 text-sm font-sans">— {o.time}</span>
                        </span>
                        <span className={`w-4 h-4 rounded-full border-2 ${form.delivery === o.id ? "border-rose-deep bg-rose-deep" : "border-ink/30"}`} />
                      </span>
                      <span className="block mt-2 text-[13px] text-ink/55">{o.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="checkout-step-payment">
                <StepTitle>{t.checkout.paymentTitle}</StepTitle>
                <div className="space-y-4 mt-2">
                  {[
                    { id: "cod", title: t.checkout.cod, desc: t.checkout.codDesc },
                    { id: "transfer", title: t.checkout.transfer, desc: t.checkout.transferDesc },
                  ].map((o) => (
                    <button
                      key={o.id}
                      onClick={() => setForm((f) => ({ ...f, payment: o.id }))}
                      data-testid={`checkout-payment-${o.id}`}
                      className={`w-full text-start border rounded-sm p-5 transition-all duration-200 ${
                        form.payment === o.id ? "border-rose-deep bg-rose/[0.06]" : "border-ink/20 hover:border-ink/40"
                      }`}
                    >
                      <span className="flex items-center justify-between">
                        <span className="font-serif text-lg text-ink">{o.title}</span>
                        <span className={`w-4 h-4 rounded-full border-2 ${form.payment === o.id ? "border-rose-deep bg-rose-deep" : "border-ink/30"}`} />
                      </span>
                      <span className="block mt-2 text-[13px] text-ink/55">{o.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="checkout-step-summary">
                <StepTitle>{t.checkout.summaryTitle}</StepTitle>
                <div className="space-y-5 mt-2 text-sm">
                  <SummaryRow label={t.checkout.stepContact} value={`${form.name} · ${form.email} · ${form.phone}`} onEdit={() => setStep(0)} editLabel={t.checkout.edit} id="checkout-edit-contact" />
                  <SummaryRow label={t.checkout.stepShipping} value={`${form.address}, ${form.city}${form.postal ? " — " + form.postal : ""}`} onEdit={() => setStep(1)} editLabel={t.checkout.edit} id="checkout-edit-shipping" />
                  <SummaryRow label={t.checkout.stepDelivery} value={`${deliveryLabel} — ${fee === 0 ? t.common.free : formatPrice(fee)}`} onEdit={() => setStep(2)} editLabel={t.checkout.edit} id="checkout-edit-delivery" />
                  <SummaryRow label={t.checkout.stepPayment} value={paymentLabel} onEdit={() => setStep(3)} editLabel={t.checkout.edit} id="checkout-edit-payment" />
                </div>
                <button onClick={placeOrder} disabled={placing} className="btn btn-ink w-full mt-9" data-testid="checkout-confirm-button">
                  {placing ? t.checkout.sending : t.checkout.placeOrder} — {formatPrice(total)}
                </button>
              </div>
            )}

            <div className="flex items-center justify-between mt-10">
              <button
                onClick={back}
                disabled={step === 0}
                className="text-[12px] uppercase tracking-[0.14em] text-ink/55 hover:text-rose-deep transition-colors disabled:opacity-0"
                data-testid="checkout-back-button"
              >
                ← {t.common.back}
              </button>
              {step < 4 && (
                <button onClick={next} className="btn btn-ink !py-3.5 !px-9" data-testid={`checkout-continue-${STEP_KEYS[step]}`}>
                  {t.checkout[`step${STEP_KEYS[step + 1].charAt(0).toUpperCase()}${STEP_KEYS[step + 1].slice(1)}`]} →
                </button>
              )}
            </div>
          </div>

          {/* side recap */}
          <aside className="bg-cream border border-ink/10 rounded-sm p-6 lg:sticky lg:top-28" data-testid="checkout-side-summary">
            <ul className="space-y-4 max-h-[340px] overflow-y-auto">
              {items.map((it, i) => (
                <li key={it.key} className="flex gap-4 items-center" data-testid={`checkout-item-${i}`}>
                  <span className="w-14 shrink-0">
                    <Placeholder variant={it.gradient} aspect="aspect-[3/4]" label="" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-serif text-[15px] text-ink truncate">{it.name[lang]}</span>
                    <span className="block text-[11.5px] text-ink/50 mt-0.5">
                      {it.size} · {COLOR_META[it.color]?.[lang]} · ×{it.qty}
                    </span>
                  </span>
                  <span className="text-[13px]">{formatPrice(it.price * it.qty)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-ink/15 space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-ink/60">{t.bag.subtotal}</span><span data-testid="checkout-subtotal">{formatPrice(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-ink/60">{t.bag.shipping}</span><span data-testid="checkout-shipping">{fee === 0 ? t.common.free : formatPrice(fee)}</span></div>
              <div className="flex justify-between pt-3 border-t border-ink/15 font-medium text-base">
                <span>{t.bag.total}</span><span data-testid="checkout-total">{formatPrice(total)}</span>
              </div>
            </div>
            <p className="mt-5 text-[11.5px] text-ink/50 leading-relaxed">{t.bag.note}</p>
          </aside>
        </div>
      </div>
    </div>
  );
}

const StepTitle = ({ children }) => <h2 className="font-serif font-light text-2xl text-ink">{children}</h2>;

const Field = ({ label, value, onChange, type = "text", id }) => (
  <div>
    <label className="field-label" htmlFor={id}>{label}</label>
    <input id={id} type={type} value={value} onChange={onChange} className="field" data-testid={id} />
  </div>
);

const SummaryRow = ({ label, value, onEdit, editLabel, id }) => (
  <div className="flex items-start justify-between gap-4 border-b border-ink/10 pb-4">
    <div>
      <p className="text-[11px] uppercase tracking-[0.14em] text-ink/45 mb-1">{label}</p>
      <p className="text-ink/80 leading-relaxed">{value}</p>
    </div>
    <button onClick={onEdit} data-testid={id} className="flex items-center gap-1 text-[11px] uppercase tracking-[0.1em] text-rose-deep hover:text-ink transition-colors shrink-0 pt-1">
      <Pencil className="w-3 h-3" strokeWidth={1.5} />
      {editLabel}
    </button>
  </div>
);
